import React, { useCallback, useMemo, useRef, useState } from 'react';
import type { InputRef } from 'antd';
import type { RefSelectProps } from 'antd/es/select';
import {
  AppButton,
  AppSelect,
  AppRadio,
  AppRadioGroup,
  AppFormLabel,
  SearchTextField,
} from '@/components/atoms';
import { Search } from '@/components/atoms/AppIcon';
import CollapsibleCard from '@/components/molecules/CollapsibleCard';
import { RA2NDatePicker } from '@/components/organisms';
import useAppModal from '@/hooks/useAppModal';
import { convGtJMonth, normalizeYearmonth } from '@/utils/dateConverter';
import dayjs from 'dayjs';
import type { MassagerCsvMassagerItem } from '../types';
import type { MassagerCsvSearchFormValues } from '../urlParams';

interface Props {
  onSearch: (values: MassagerCsvSearchFormValues) => void;
  onVisitmonthChange?: (isoMonth: string | null) => void;
  massagers: MassagerCsvMassagerItem[];
  isLoading: boolean;
  cardTitle: string;
  initialValues?: MassagerCsvSearchFormValues | null;
  totalCount?: number;
}

const MassagerCsvSearchForm: React.FC<Props> = ({
  onSearch,
  onVisitmonthChange,
  massagers,
  isLoading,
  cardTitle,
  initialValues,
  totalCount,
}) => {
  const modal = useAppModal();

  const defaultMonth = useMemo(() => convGtJMonth(dayjs().startOf('month')), []);

  const [form, setForm] = useState<MassagerCsvSearchFormValues>(
    () =>
      initialValues ?? {
        searchVisitmonth: defaultMonth,
        serialNumber: '',
        massagerId: null,
        rezeptKbn: '1',
        sortRadio: '1',
      }
  );

  const updateField = useCallback(
    <K extends keyof MassagerCsvSearchFormValues>(
      key: K,
      value: MassagerCsvSearchFormValues[K]
    ) => {
      setForm(prev => ({ ...prev, [key]: value }));
    },
    []
  );

  const massagerOptions = useMemo(
    () => [
      { value: '', label: 'すべて' },
      ...massagers.map(m => ({
        value: m.id,
        label: `${m.serial_number ?? ''}:${m.name}`,
      })),
    ],
    [massagers]
  );

  const handleSearch = useCallback(() => {
    const payloadMonth = normalizeYearmonth(form.searchVisitmonth);
    if (!form.searchVisitmonth?.trim()) {
      modal.error({ title: '【施術年月】を入力してください', okText: 'OK' });
      return;
    }
    if (!payloadMonth) {
      modal.error({ title: '【施術年月】を正しく入力してください', okText: 'OK' });
      return;
    }

    onSearch({ ...form, searchVisitmonth: payloadMonth });
  }, [form, modal, onSearch]);

  // Enter key = move focus to next field (Tab-like behavior per legacy RA2 spec)
  const formRef = useRef<HTMLDivElement>(null);
  const serialNumberRef = useRef<InputRef>(null);
  const massagerRef = useRef<RefSelectProps>(null);
  const visitmonthRef = useRef<InputRef>(null);
  const rezeptKbnRef = useRef<HTMLDivElement>(null);
  const sortRadioRef = useRef<HTMLDivElement>(null);
  const searchButtonRef = useRef<HTMLButtonElement>(null);

  const focusCheckedRadio = useCallback((container: HTMLDivElement | null) => {
    const checkedRadio = container?.querySelector<HTMLInputElement>('input[type="radio"]:checked');
    const firstRadio = container?.querySelector<HTMLInputElement>('input[type="radio"]');
    (checkedRadio ?? firstRadio)?.focus();
  }, []);

  const focusNextField = useCallback(
    (currentIndex: string | null) => {
      switch (currentIndex) {
        case '0':
          massagerRef.current?.focus();
          break;
        case '1':
          visitmonthRef.current?.focus();
          break;
        case '2':
          focusCheckedRadio(rezeptKbnRef.current);
          break;
        case '3':
          focusCheckedRadio(sortRadioRef.current);
          break;
        case '4':
          searchButtonRef.current?.focus();
          break;
        default:
          break;
      }
    },
    [focusCheckedRadio]
  );

  const handleVisitmonthEnter = useCallback(() => {
    window.setTimeout(() => {
      const nativeInput = visitmonthRef.current?.nativeElement as HTMLInputElement | undefined;
      const committedValue =
        visitmonthRef.current?.input?.value ?? nativeInput?.value ?? form.searchVisitmonth;

      if (normalizeYearmonth(committedValue)) {
        focusCheckedRadio(rezeptKbnRef.current);
      }
    }, 0);
  }, [focusCheckedRadio, form.searchVisitmonth]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      if (!(e.target instanceof Element)) return;

      if (e.target.closest('[data-enter-skip="true"]')) {
        return;
      }

      const currentField = e.target.closest<HTMLElement>('[data-focus-index]');
      if (!currentField) return;

      e.preventDefault();
      focusNextField(currentField.dataset.focusIndex ?? null);
    },
    [focusNextField]
  );

  return (
    <CollapsibleCard title={cardTitle}>
      <div ref={formRef} onKeyDown={handleKeyDown}>
        <div className="flex flex-col gap-4">
          {/* Row 1: 受診者番号 / 施術師 / 施術年月 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            <div className="flex flex-col min-w-0 gap-1 sm:flex-row sm:items-center sm:gap-3">
              <AppFormLabel label="受診者番号" className="text-sm shrink-0 sm:w-24" />
              <SearchTextField
                ref={serialNumberRef}
                data-focus-index="0"
                value={form.serialNumber}
                onChange={e => updateField('serialNumber', e.target.value)}
                className="w-full sm:flex-1"
              />
            </div>

            <div className="flex flex-col min-w-0 gap-1 sm:flex-row sm:items-center sm:gap-3">
              <AppFormLabel label="施術師" className="text-sm shrink-0 sm:w-20" />
              <AppSelect
                ref={massagerRef}
                data-focus-index="1"
                className="w-full sm:flex-1"
                value={form.massagerId ?? undefined}
                onChange={val => updateField('massagerId', val ? Number(val) : null)}
                options={massagerOptions}
              />
            </div>

            <div
              className="flex flex-col min-w-0 gap-1 sm:flex-row sm:items-center sm:gap-3 md:col-span-2 xl:col-span-1"
              data-focus-index="2"
              data-enter-skip="true"
            >
              <AppFormLabel label="施術年月" className="text-sm shrink-0 sm:w-24" />
              <RA2NDatePicker
                ref={visitmonthRef}
                picker="month"
                className="w-full sm:flex-1"
                placeholder="元号00年00月00日"
                value={form.searchVisitmonth}
                onChange={val => {
                  updateField('searchVisitmonth', val);
                  onVisitmonthChange?.(normalizeYearmonth(val));
                }}
                onEnter={handleVisitmonthEnter}
              />
            </div>
          </div>

          {/* Row 2: レセプト区分 / 並び順 / 該当件数 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,320px)_minmax(0,1fr)] 2xl:grid-cols-[minmax(0,320px)_minmax(0,1fr)_auto] 2xl:items-center">
            {/* レセプト区分 */}
            <div
              ref={rezeptKbnRef}
              data-focus-index="3"
              className="flex min-h-[40px] min-w-0 items-center rounded border border-gray-200 bg-white px-4 py-2"
            >
              <AppRadioGroup
                value={form.rezeptKbn}
                onChange={e => updateField('rezeptKbn', e.target.value as '1' | '2')}
                className="flex flex-wrap gap-x-4 gap-y-1"
              >
                <AppRadio value="1">本レセ</AppRadio>
                <AppRadio value="2">関レセ</AppRadio>
              </AppRadioGroup>
            </div>

            {/* 並び順 */}
            <div
              ref={sortRadioRef}
              data-focus-index="4"
              className="flex min-h-[40px] min-w-0 items-center rounded border border-gray-200 bg-white px-4 py-2"
            >
              <AppRadioGroup
                value={form.sortRadio}
                onChange={e => updateField('sortRadio', e.target.value as '1' | '2' | '3' | '4')}
                className="flex flex-wrap min-w-0 gap-x-6 gap-y-1"
              >
                <AppRadio value="1">受診者番号順</AppRadio>
                <AppRadio value="2">仮名順</AppRadio>
                <AppRadio value="3">保険者番号順</AppRadio>
                <AppRadio value="4">施術師順</AppRadio>
              </AppRadioGroup>
            </div>

            {/* 該当件数 */}
            <div className="flex justify-center shrink-0 md:col-span-2 md:justify-end 2xl:col-span-1">
              <div className="px-3 py-1 text-sm font-medium leading-tight text-center text-black rounded-md bg-amber-400">
                該当件数 {totalCount ?? 0}
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-center mt-4">
          <AppButton
            ref={searchButtonRef}
            type="primary"
            onClick={handleSearch}
            loading={isLoading}
            className="w-full sm:w-auto"
          >
            <Search /> 検索
          </AppButton>
        </div>
      </div>
    </CollapsibleCard>
  );
};

export default MassagerCsvSearchForm;
